import { useEffect, useState, useCallback } from "react";
import { useLang } from "../hooks/useLang";
import {
  listDocuments, listTranslations, translateDocument, documentDocxURL,
  type GeneratedDocMeta, type TranslationMeta,
} from "../services/documents";
import { getMe, type Me } from "../services/api";
import Icon from "./icons";

// Hedef dil seçenekleri — MDR Annex I resmi dili İngilizce; çeviri distribütör/
// yerel otorite için ek hizmet. Yalnız doldurulan (üretilen) kısımlar çevrilir; iskelet EN kalır.
const LANGS = [
  { code: "tr", label: "Türkçe" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "ru", label: "Русский" },
  { code: "ar", label: "العربية" },
];
const langLabel = (code?: string) => LANGS.find((l) => l.code === code)?.label || (code || "").toUpperCase();

// DocTranslate — 2 tab: "Çevir" (kaynak GSPR seç → dile çevir) ve "Çevrilenler"
// (tamamlanan çeviriler; orijinal ad, orijinal + hedef dil, indirilebilir).
export default function DocTranslate() {
  const { t } = useLang();
  const [tab, setTab] = useState<"translate" | "translated">("translate");
  const [docs, setDocs] = useState<GeneratedDocMeta[]>([]);
  const [translations, setTranslations] = useState<TranslationMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [target, setTarget] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);

  const price = me?.price_translation_credits ?? 100;
  const active = !!me?.translation_active;

  const reloadTranslations = useCallback(() => {
    listTranslations().then((r) => setTranslations(r.translations || [])).catch(() => {});
  }, []);

  useEffect(() => {
    listDocuments()
      .then((r) => setDocs((r.documents || []).filter((d) => d.status !== "preparing")))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    reloadTranslations();
    getMe().then(setMe).catch(() => {});
  }, [reloadTranslations]);

  // Hazırlanan çeviri varsa periyodik yenile (bitince listede "Hazır" görünsün).
  useEffect(() => {
    if (!translations.some((x) => x.status === "preparing")) return;
    const iv = setInterval(reloadTranslations, 5000);
    return () => clearInterval(iv);
  }, [translations, reloadTranslations]);

  async function onTranslate(docId: number) {
    setErr(null); setNotice(null); setBusyId(docId);
    try {
      await translateDocument(docId, target[docId] || "tr");
      setNotice(t("translate_started"));
      reloadTranslations();
      setTab("translated");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  const fmtDate = (s: string) => {
    try { return new Date(s).toLocaleDateString("tr-TR"); } catch { return s; }
  };

  return (
    <div style={{ maxWidth: "100%", margin: "0 auto" }}>
      <h2 style={{ marginBottom: 4 }}>{t("translate_title")}</h2>
      <p className="hint" style={{ marginBottom: 12 }}>{t("translate_sub")}</p>

      <div className="da-cost-badge">🪙 {t("translate_cost_badge").replace("{n}", String(price))}</div>
      {!active && <div className="da-soon-notice">⏳ {t("translate_disabled_note")}</div>}
      {notice && <div className="da-info-notice">✅ {notice}</div>}
      {err && <div className="error">{err}</div>}

      <div className="dt-tabs">
        <button className={"dt-tab" + (tab === "translate" ? " active" : "")} onClick={() => setTab("translate")}>
          {t("translate_tab_translate")}
        </button>
        <button className={"dt-tab" + (tab === "translated" ? " active" : "")} onClick={() => { setTab("translated"); reloadTranslations(); }}>
          {t("translate_tab_translated")}{translations.length ? ` (${translations.length})` : ""}
        </button>
      </div>

      {tab === "translate" ? (
        loading ? (
          <div className="loading"><div className="spinner" /><div>{t("translate_loading")}</div></div>
        ) : docs.length === 0 ? (
          <div className="empty" style={{ padding: "60px 20px" }}>
            <Icon name="languages" size={38} />
            <h3>{t("translate_empty")}</h3>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="dt-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1.5px solid var(--border)" }}>
                  <th style={{ padding: "8px 10px" }}>{t("translate_col_doc")}</th>
                  <th style={{ padding: "8px 10px" }}>{t("translate_col_date")}</th>
                  <th style={{ padding: "8px 10px" }}>{t("translate_col_lang")}</th>
                  <th style={{ padding: "8px 10px" }}>{t("translate_col_target")}</th>
                  <th style={{ padding: "8px 10px" }}></th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "9px 10px" }}>
                      <span style={{ fontWeight: 600 }}>{d.product_name}{d.document_code ? ` (${d.document_code})` : ""}</span>
                    </td>
                    <td style={{ padding: "9px 10px", color: "var(--fg-mute)" }}>{fmtDate(d.created_at)}</td>
                    <td style={{ padding: "9px 10px" }}><span className="badge badge-src">EN</span></td>
                    <td style={{ padding: "9px 10px" }}>
                      <select
                        className="dt-select"
                        value={target[d.id] || "tr"}
                        onChange={(e) => setTarget((p) => ({ ...p, [d.id]: e.target.value }))}
                        disabled={!active || busyId === d.id}
                      >
                        {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "9px 10px" }}>
                      <button
                        className="btn-pri"
                        disabled={!active || busyId === d.id}
                        onClick={() => onTranslate(d.id)}
                        title={active ? "" : t("translate_disabled_note")}
                      >
                        {busyId === d.id ? (<><span className="spinner-sm" /> {t("translate_btn")}</>) : (<>{t("translate_btn")}{active ? ` — ${price} ${t("credits_short")}` : ""}</>)}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        translations.length === 0 ? (
          <div className="empty" style={{ padding: "60px 20px" }}>
            <Icon name="languages" size={38} />
            <h3>{t("translated_empty")}</h3>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="dt-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1.5px solid var(--border)" }}>
                  <th style={{ padding: "8px 10px" }}>{t("translate_col_doc")}</th>
                  <th style={{ padding: "8px 10px" }}>{t("translate_col_langs")}</th>
                  <th style={{ padding: "8px 10px" }}>{t("translate_col_status")}</th>
                  <th style={{ padding: "8px 10px" }}>{t("translate_col_date")}</th>
                  <th style={{ padding: "8px 10px" }}></th>
                </tr>
              </thead>
              <tbody>
                {translations.map((x) => {
                  const ready = x.status !== "preparing";
                  return (
                    <tr key={x.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "9px 10px", fontWeight: 600 }}>{x.product_name}</td>
                      <td style={{ padding: "9px 10px" }}>
                        <span className="badge badge-src">EN</span>
                        <span style={{ margin: "0 6px", color: "var(--fg-mute)" }}>→</span>
                        <span className="badge badge-app">{langLabel(x.language)}</span>
                      </td>
                      <td style={{ padding: "9px 10px" }}>
                        {ready
                          ? <span className="badge badge-app">✓ {t("translate_status_ready")}</span>
                          : <span className="badge" style={{ color: "var(--fg-mute)" }}><span className="spinner-sm" /> {t("translate_status_preparing")}</span>}
                      </td>
                      <td style={{ padding: "9px 10px", color: "var(--fg-mute)" }}>{fmtDate(x.created_at)}</td>
                      <td style={{ padding: "9px 10px" }}>
                        {ready ? (
                          <a className="btn-sec" href={documentDocxURL(x.id)}>⬇ {t("da_download_docx")}</a>
                        ) : (
                          <button className="btn-sec" disabled>{t("translate_status_preparing")}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
